package co.maytes.standupbot.dashboard.pages.app.onboarding

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.TextInput
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideClock
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideMessagesSquare
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideTag
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideUsers
import co.maytes.standupbot.dashboard.features.onboarding.FlowNode
import co.maytes.standupbot.dashboard.features.onboarding.NoticeTone
import co.maytes.standupbot.dashboard.features.onboarding.OnboardingShell
import co.maytes.standupbot.dashboard.features.onboarding.OnboardingStep
import co.maytes.standupbot.dashboard.features.onboarding.OptionCard
import co.maytes.standupbot.dashboard.features.onboarding.SchedulePresets
import co.maytes.standupbot.dashboard.features.onboarding.WizardBrand
import co.maytes.standupbot.dashboard.features.onboarding.WizardFlow
import co.maytes.standupbot.dashboard.features.onboarding.WizardNotice
import co.maytes.standupbot.dashboard.features.onboarding.WizardSection
import co.maytes.standupbot.dashboard.features.onboarding.rememberOnboardingStore
import co.maytes.standupbot.dashboard.features.roster.ChannelRosterSection
import co.maytes.standupbot.dashboard.features.roster.RosterPickerState
import co.maytes.standupbot.dashboard.features.roster.rememberChannelRosterStore
import co.maytes.standupbot.dashboard.features.roster.rememberLinearUsersStore
import co.maytes.standupbot.dashboard.features.standups.ChannelPicker
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.shared.dto.standup.CreateStandupRequestDto
import co.maytes.standupbot.shared.dto.standup.RosterSeedDto
import co.maytes.standupbot.shared.dto.standup.StandupDto
import co.maytes.standupbot.shared.dto.standup.StandupScheduleDto
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.navigation.Link
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div

@Page("/app/onboarding/setup")
@Composable
fun OnboardingSetupPage() {
    val api = rememberApiClient()
    val ui = rememberUiStore()
    val auth = rememberAuthStore()
    val store = rememberOnboardingStore()
    val rosterStore = rememberChannelRosterStore()
    val linearUsers = rememberLinearUsersStore()
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    val workspaceId = auth.currentUser?.workspace?.id
    val defaultTz = auth.currentUser?.timezone ?: "UTC"

    var selected by remember { mutableStateOf(emptySet<String>()) }
    var creating by remember { mutableStateOf(false) }

    LaunchedEffect(workspaceId, defaultTz) {
        if (store.scheduleTimezone == "UTC" && defaultTz != "UTC") {
            store.setScheduleTimezone(defaultTz)
        }
    }

    val alreadyCreated = store.createdStandupId != null
    val channelValid = !store.selectedChannelId.isNullOrBlank()
    val nameValid = store.standupName.isNotBlank()
    val scheduleValid = store.scheduleDays.isNotEmpty()
    val rosterReady = rosterStore.state is RosterPickerState.Ready
    val canContinue = alreadyCreated || (channelValid && nameValid && scheduleValid && rosterReady && !creating)

    OnboardingShell(
        step = OnboardingStep.Setup,
        headline = "Set up your standup",
        subline = "Pick a channel, who is on it, and when it runs.",
        art = {
            WizardFlow(
                nodes = listOf(
                    FlowNode(WizardBrand.Linear, live = true),
                    FlowNode(WizardBrand.StandupBot),
                    FlowNode(WizardBrand.Slack, live = true),
                ),
            )
        },
        nextEnabled = canContinue,
        nextLoading = creating,
        nextLabel = if (creating) "Creating…" else "Continue",
        onNext = {
            if (alreadyCreated) {
                ctx.router.navigateTo(OnboardingStep.Preview.path)
                return@OnboardingShell
            }
            val channelId = store.selectedChannelId
            if (!canContinue || workspaceId == null || channelId == null) return@OnboardingShell
            scope.launch {
                creating = true
                val name = store.standupName.trim()
                val outcome = api.post<CreateStandupRequestDto, StandupDto>(
                    path = "/api/v1/workspaces/$workspaceId/standups",
                    body = CreateStandupRequestDto(
                        name = name,
                        slackChannelId = channelId,
                        slackChannelName = store.selectedChannelName,
                        schedule = StandupScheduleDto(
                            daysOfWeek = store.scheduleDays,
                            hour = store.scheduleHour,
                            minute = store.scheduleMinute,
                        ),
                        timezone = store.scheduleTimezone,
                        templateId = null,
                        roster = RosterSeedDto(includeCreator = true, slackUserIds = selected.toList()),
                    ),
                )
                creating = false
                when (outcome) {
                    is Result.Ok -> {
                        store.setCreatedStandup(
                            id = outcome.value.id,
                            name = name,
                            roster = outcome.value.roster,
                            nudge = outcome.value.upgradeNudge,
                        )
                        ctx.router.navigateTo(OnboardingStep.Preview.path)
                    }
                    is Result.Err -> ui.showToast(
                        "Couldn't create standup: ${outcome.error.displayMessage}",
                        ToastKind.Error,
                    )
                }
            }
        },
    ) {
        if (alreadyCreated) {
            AlreadyCreatedNotice(
                name = store.createdStandupName ?: store.standupName,
                channelName = store.selectedChannelName,
                standupId = store.createdStandupId,
            )
        } else {
            WizardSection(title = "Slack channel", hint = "Where the standup posts.", icon = { LucideMessagesSquare(size = 18) }) {
                ChannelPicker(
                    workspaceId = workspaceId,
                    selectedId = store.selectedChannelId,
                    selectedName = store.selectedChannelName,
                    onChange = { id, name ->
                        val changed = id != store.selectedChannelId
                        store.setChannel(id, name)
                        if (changed) selected = emptySet()
                    },
                )
            }

            WizardSection(
                title = "Who is on it",
                hint = "Everyone in the channel is picked. Guests are left unchecked.",
                icon = { LucideUsers(size = 18) },
            ) {
                ChannelRosterSection(
                    store = rosterStore,
                    workspaceId = workspaceId,
                    channelId = store.selectedChannelId,
                    channelName = store.selectedChannelName,
                    selected = selected,
                    onSelectionChange = { selected = it },
                    linearUsers = linearUsers,
                )
            }

            WizardSection(
                title = "Schedule",
                hint = "Timezone: ${store.scheduleTimezone}. Fine-tune later in Settings → Teams.",
                icon = { LucideClock(size = 18) },
            ) {
                SchedulePicker(
                    selectedDays = store.scheduleDays,
                    selectedHour = store.scheduleHour,
                    selectedMinute = store.scheduleMinute,
                    onChange = { days, hour, minute ->
                        store.setSchedule(days, hour, minute, store.scheduleTimezone)
                    },
                )
            }

            WizardSection(title = "Name", hint = "Defaults to the channel name.", icon = { LucideTag(size = 18) }) {
                TextInput(
                    value = store.standupName,
                    onValueChange = { store.setStandupName(it) },
                    placeholder = "e.g. eng-standup",
                    ariaLabel = "Standup name",
                )
            }
        }
    }
}

@Composable
private fun AlreadyCreatedNotice(name: String, channelName: String?, standupId: String?) {
    WizardNotice(
        tone = NoticeTone.Success,
        title = "\"$name\" is already created${channelName?.let { " for #$it" }.orEmpty()}",
        message = "Continue to post it.",
        action = {
            Link(
                path = standupId?.let { "/app/standups/$it" } ?: "/app/standups",
                text = "Edit standup",
            )
        },
    )
}

@Composable
private fun SchedulePicker(
    selectedDays: List<String>,
    selectedHour: Int,
    selectedMinute: Int,
    onChange: (days: List<String>, hour: Int, minute: Int) -> Unit,
) {
    Div(attrs = { classes("sb-wiz-stack") }) {
        SchedulePresets.all.forEach { preset ->
            OptionCard(
                title = preset.timeLabel,
                description = SchedulePresets.describeDays(preset.days),
                selected = preset.matches(selectedDays, selectedHour, selectedMinute),
                onSelect = { onChange(preset.days, preset.hour, preset.minute) },
                leading = { LucideClock(size = 18) },
            )
        }
    }
}
